import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { Icon } from '@/components/business/icon'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useWechatMpStatus } from '@/features/notification/api'
import { shouldShowWechatGuide } from '@/features/notification/wechat-status'
import { clearConsultSessionUnreadCache, getConsultUnreadQueryKey } from '@/features/consult/api'
import {
  type NotificationCenterItem,
  useNotificationCenterStore,
} from '@/features/notification/notification-center-store'
import { NOTIFICATION_CENTER_TOUR_EVENT } from '@/features/notification/notification-tour-events'
import { useAuthStore } from '@/stores/auth'

interface DisplayNotification {
  id: string
  kind: NotificationCenterItem['kind']
  title: string
  description: string
  time: string
  target?: string
  sessionId?: string
  routeRole?: 'user' | 'lawyer'
  unread: boolean
  icon: string
  onRead: () => void
}

function formatNotifyTime(value: string | undefined): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

function clampBadgeCount(count: number): string {
  if (count <= 0) return ''
  return count > 99 ? '99+' : String(count)
}

function buildConsultTarget(routeRole: 'user' | 'lawyer', sessionId: string): string {
  const base = routeRole === 'lawyer' ? '/lawyer/consult' : '/u/consult'
  return `${base}?session=${encodeURIComponent(sessionId)}`
}

function resolveLocalTarget(item: NotificationCenterItem): string | undefined {
  if (item.target) return item.target
  if (item.kind === 'consult' && item.routeRole && item.sessionId) {
    return buildConsultTarget(item.routeRole, item.sessionId)
  }
  return undefined
}

function fromLocalItem(item: NotificationCenterItem, onRead: () => void): DisplayNotification {
  return {
    id: item.id,
    kind: item.kind,
    title: item.title,
    description: item.description,
    time: formatNotifyTime(item.createdAt),
    target: resolveLocalTarget(item),
    sessionId: item.sessionId,
    routeRole: item.routeRole,
    unread: !item.read,
    icon: item.kind === 'consult' ? 'chat' : 'notifications',
    onRead,
  }
}

export function NotificationCenterBell() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const hasToken = useAuthStore((s) => Boolean(s.accessToken))
  const localItems = useNotificationCenterStore((s) => s.items)
  const localUnread = useNotificationCenterStore((s) => s.unreadCount)
  const markRead = useNotificationCenterStore((s) => s.markRead)
  const markAllRead = useNotificationCenterStore((s) => s.markAllRead)
  const wechatStatusQuery = useWechatMpStatus({}, { enabled: hasToken && open })

  const totalUnread = localUnread
  const badgeText = clampBadgeCount(totalUnread)
  const shouldShowFollowPrompt =
    hasToken && !wechatStatusQuery.isError && shouldShowWechatGuide(wechatStatusQuery.data)

  const displayItems = useMemo<DisplayNotification[]>(() => {
    const local = localItems.map((item) =>
      fromLocalItem(item, () => {
        markRead(item.id)
      }),
    )
    return local.slice(0, 12)
  }, [localItems, markRead])

  useEffect(() => {
    function handleTourOpenChange(event: Event) {
      const detail = (event as CustomEvent<{ open?: unknown }>).detail
      if (typeof detail?.open === 'boolean') {
        setOpen(detail.open)
      }
    }

    window.addEventListener(NOTIFICATION_CENTER_TOUR_EVENT, handleTourOpenChange)
    return () => window.removeEventListener(NOTIFICATION_CENTER_TOUR_EVENT, handleTourOpenChange)
  }, [])

  function refreshConsultUnread(item: DisplayNotification) {
    if (item.kind !== 'consult') return
    if (item.routeRole && item.sessionId) {
      clearConsultSessionUnreadCache(
        queryClient,
        item.routeRole,
        item.sessionId,
        item.unread ? 1 : 0,
      )
      void queryClient.invalidateQueries({ queryKey: ['consult', 'sessions', item.routeRole] })
      void queryClient.invalidateQueries({ queryKey: getConsultUnreadQueryKey(item.routeRole) })
      return
    }
    void queryClient.invalidateQueries({ queryKey: ['consult', 'sessions'] })
  }

  function handleOpenNotification(item: DisplayNotification) {
    item.onRead()
    refreshConsultUnread(item)
    setOpen(false)
    if (item.target) navigate(item.target)
  }

  function handleMarkAllRead() {
    markAllRead()
  }

  if (!hasToken) return null

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-tour="notification-bell"
          className="text-on-surface-variant hover:text-on-surface focus-visible:ring-primary/25 relative flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-white/45 focus-visible:ring-2 focus-visible:outline-none dark:hover:bg-white/[0.10]"
          aria-label="通知中心"
          title="通知中心"
        >
          <Icon name={badgeText ? 'notifications_active' : 'notifications'} className="text-lg" />
          {badgeText && (
            <span className="bg-error text-on-error absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-4 font-semibold">
              {badgeText}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={10}
        className="w-[min(360px,calc(100vw-2rem))] p-0"
      >
        <div className="border-outline-variant/50 flex items-center justify-between border-b px-4 py-3">
          <div>
            <h2 className="text-on-surface font-display text-base font-semibold">通知中心</h2>
            <p className="text-on-surface-variant mt-0.5 text-xs">
              {totalUnread > 0 ? `${totalUnread} 条未读` : '暂无未读'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleMarkAllRead}
            disabled={totalUnread === 0}
            className="text-primary disabled:text-on-surface-variant rounded-full px-3 py-1.5 text-xs font-medium transition-colors hover:bg-white/45 disabled:cursor-not-allowed dark:hover:bg-white/[0.10]"
          >
            全部已读
          </button>
        </div>

        <div className="max-h-[min(70dvh,520px)] overflow-y-auto px-3 py-3">
          {shouldShowFollowPrompt && (
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                navigate('/settings/notification')
              }}
              className="content-card mb-3 w-full rounded-2xl p-3 text-left transition-colors hover:bg-white/55 dark:hover:bg-white/[0.10]"
            >
              <div className="flex items-start gap-3">
                <div className="glass-field text-primary mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
                  <Icon name="notifications_active" />
                </div>
                <div className="min-w-0">
                  <p className="text-on-surface text-sm font-medium">
                    {wechatStatusQuery.data?.action === 'bind'
                      ? '关联服务号'
                      : wechatStatusQuery.data?.action === 'select_account'
                        ? '选择服务号'
                        : '关注服务号'}
                  </p>
                  <p className="text-on-surface-variant mt-1 text-xs leading-5">
                    离线时接收律师回复提醒，避免错过咨询消息。
                  </p>
                </div>
              </div>
            </button>
          )}

          {displayItems.length > 0 ? (
            <div className="space-y-1">
              {displayItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleOpenNotification(item)}
                  className={cn(
                    'group flex w-full items-start gap-3 rounded-2xl px-2.5 py-3 text-left transition-colors hover:bg-white/45 dark:hover:bg-white/[0.10]',
                    item.unread && 'bg-primary/5',
                  )}
                >
                  <span className="glass-field text-primary mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
                    <Icon name={item.icon} className="text-base" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-start justify-between gap-2">
                      <span className="text-on-surface line-clamp-1 text-sm font-medium">
                        {item.title}
                      </span>
                      {item.unread && (
                        <span className="bg-error mt-1 h-2 w-2 shrink-0 rounded-full" />
                      )}
                    </span>
                    <span className="text-on-surface-variant mt-1 line-clamp-2 text-xs leading-5">
                      {item.description}
                    </span>
                    {item.time && (
                      <span className="text-outline mt-1 block text-xs leading-4">{item.time}</span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex min-h-48 flex-col items-center justify-center px-6 py-8 text-center">
              <div className="glass-field text-on-surface-variant flex h-12 w-12 items-center justify-center rounded-full">
                <Icon name="notifications" className="text-xl" />
              </div>
              <p className="text-on-surface mt-3 text-sm font-medium">暂无通知</p>
              <p className="text-on-surface-variant mt-1 text-xs leading-5">
                新咨询消息和系统通知会显示在这里。
              </p>
            </div>
          )}
        </div>

        <div className="border-outline-variant/50 flex items-center justify-between border-t px-4 py-3">
          <button
            type="button"
            data-tour="notification-settings-entry"
            onClick={() => {
              setOpen(false)
              navigate('/settings/notification')
            }}
            className="text-primary rounded-full px-3 py-1.5 text-xs font-medium transition-colors hover:bg-white/45 dark:hover:bg-white/[0.10]"
          >
            通知设置
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
